import { Group, Button, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo, useState } from "react";
import {
  CreateLocationOfferingInput,
  GetAgreementNamesQuery,
  useCreateLocationOfferingMutation,
  useGetAgreementNamesQuery,
  useGetLocationOfferingDetailQuery,
  useUpdateLocationOfferingMutation,
} from "src/generated/graphql";
import { array, object, string } from "yup";
import { closeAllModals } from "@mantine/modals";
import Select from "src/components/Select";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import ActionCard from "src/components/ActionCard";
import { IoIosRemoveCircle } from "react-icons/io";
import { CardRadius, HeightCard } from "src/utils/constants";

interface IFormProps
  extends Omit<
    CreateLocationOfferingInput,
    "agreement" | "location" | "details"
  > {
  agreement: string;
  location: string;
  details: { name: string }[];
}

export const LocationOfferingFormSchema = object().shape({
  agreement: string().required("Agreement is required"),
  header: string()
    .trim("No spaces allowed")
    .strict(true)
    .required("Header is required")
    .max(255),
  name: string().trim("No spaces allowed").strict(true).nullable().max(30),
  subtext: string().nullable().max(255),
  details: array().of(
    object().shape({
      name: string()
        .trim("No spaces allowed")
        .strict(true)
        .min(2, "Detail should must be least 2 letters")
        .max(30, "Detail must not be greater than 30 letters")
        .required("Detail is required"),
    })
  ),
});

const defaultLocationOffering = {
  name: "",
  header: "",
  subtext: "",
  agreement: "",
  location: "",
  promoCode: "",
  details: [{ name: "" }],
};

interface IProps {
  offeringId?: string;
  locationId: string;
  refetch: () => void;
  savedAgreements: string[];
}

const OfferingDetail = ({
  offeringId,
  locationId,
  refetch,
  savedAgreements,
}: IProps) => {
  const theme = useMantineTheme();
  const [agreements, setagreements] = useState<
    GetAgreementNamesQuery["agreementMany"]
  >([]);
  const form = useForm<IFormProps>({
    initialValues: defaultLocationOffering,
    validate: yupResolver(LocationOfferingFormSchema),
  });

  useGetAgreementNamesQuery({
    variables: { locationIds: [{ id: locationId }] },
    skip: !locationId,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      // filter out already added agreements
      const filteredData =
        data.agreementMany?.filter(
          (ag) => !savedAgreements.find((e) => ag.id === e)
        ) || [];
      setagreements(filteredData);
    },
  });

  const { data: offeringDetail } = useGetLocationOfferingDetailQuery({
    variables: { id: offeringId || "" },
    skip: !offeringId,
    fetchPolicy: "cache-and-network",
    onCompleted: () => {
      resetFormState();
    },
  });

  const [createOffering, { loading: createLoading }] =
    useCreateLocationOfferingMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const [updateOffering, { loading: editLoading }] =
    useUpdateLocationOfferingMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const resetFormState = () => {
    const { details, agreement, ...rest } =
      offeringDetail?.locationOfferingOne || {};
    const _detail = details?.map((d: string) => ({ name: d })) || [];
    let _data = {
      agreement: agreement!.id,
      details: _detail,
      ...rest,
    };
    form.setValues(_data);
    form.resetDirty(_data as IFormProps);
  };

  const handleSubmit = async (values: IFormProps) => {
    if (!locationId) return;
    const { agreement, details, location, ...rest } = values;
    const obj = {
      agreement: { id: agreement! },
      details: Object.values(details).map((e) => e.name),
      location: { id: locationId },
      ...rest,
    };
    offeringId
      ? updateOffering({
          variables: {
            data: obj,
            id: offeringId,
          },
        })
      : createOffering({
          variables: {
            data: obj,
          },
        });
  };

  const ValuesArr = form.getInputProps("details").value;
  const handleAddOption = () => {
    form.insertListItem("details", { name: "" });
  };

  const handleDeleteOption = (index: number) => {
    form.removeListItem("details", index);
  };

  return (
    <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
      <TextInput label="Name" {...form.getInputProps("name")} />

      <Select
        withAsterisk
        data={
          agreements?.map((val) => ({
            value: val?.id,
            label: val.name,
          })) || []
        }
        label="Agreement"
        {...form.getInputProps("agreement")}
      />

      <TextInput
        withAsterisk
        label="Header"
        {...form.getInputProps("header")}
      />

      <Textarea label="Description" {...form.getInputProps("subtext")} />

      <TextInput label="PromoCode" {...form.getInputProps("promoCode")} />

      <ActionCard
        mt={"md"}
        title="Details"
        handleModal={handleAddOption}
        height={HeightCard.md}
        radius={CardRadius.md}
        size="md"
      >
        {ValuesArr?.map((e: string, index: number) => (
          <TextInput
            key={index}
            withAsterisk
            label={"Detail"}
            rightSection={
              ValuesArr.length > 1 && (
                <IoIosRemoveCircle
                  color={theme.colors.red[6]}
                  size={20}
                  onClick={() => handleDeleteOption(index)}
                />
              )
            }
            {...form.getInputProps(`details.${index}.name`)}
          />
        ))}
      </ActionCard>

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={() => closeAllModals()}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={createLoading || editLoading}
        >
          {offeringId ? "Save" : "Create"}
        </Button>
      </Group>
    </form>
  );
};

export default memo(OfferingDetail);
